<template>
  <section aria-label="最近任务" class="ai-menu-v2__recent" :class="{ 'is-mobile': mobile }">
    <div class="ai-menu-v2__recent-title">最近任务</div>
    <div class="ai-menu-v2__recent-list">
      <button
        v-for="session in recentSessions"
        :key="session.sessionId"
        class="ai-menu-v2__recent-item"
        :class="{ active: appStore.aiSalesMentorSession?.sessionId === session.sessionId }"
        :title="session.title || '未知标题会话'"
        type="button"
        @click="handleClickSession(session)"
      >
        <i
          v-if="session.sourceType === 'schedule'"
          aria-hidden="true"
          class="icon-timer-line t-iconfont"
        ></i>
        <span class="ai-menu-v2__text">{{ session.title || '未知标题会话' }}</span>
        <div class="right-box">
          <span v-if="!session.isRead" class="ai-menu-v2__unread"></span>
        </div>
      </button>
      <div v-if="!recentSessions.length" class="ai-menu-v2__session-status">暂无会话</div>
    </div>
  </section>
</template>

<script setup>
import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { getSessionList, updateSession } from '@/api/new-agent/qca.js'
import { useAppStore } from '@/store/useAppStore'
import emitter from '@/utils/mitt'
// import utils from '@/utils'

defineOptions({ name: 'RecentConversations' })
const props = defineProps({ mobile: Boolean })
const emits = defineEmits(['handleOpenAiAgent', 'navigate', 'updated', 'selected'])
const appStore = useAppStore()
const route = useRoute()

const recentSessions = ref([])
const sessionPage = ref(1)
const sessionPageSize = 20
const sessionsLoading = ref(false)
const sessionsFinished = ref(false)
let sessionsRequest = null
let isUnmounted = false
let pageActiveTimer = null

const mergeFirstPageSessions = (sessionList) => {
  const firstPageIds = new Set(sessionList.map((session) => session.sessionId))
  recentSessions.value = [
    ...sessionList,
    ...recentSessions.value.filter((session) => !firstPageIds.has(session.sessionId))
  ]
}

const appendSessions = (sessionList) => {
  const knownIds = new Set(recentSessions.value.map((session) => session.sessionId))
  sessionList.forEach((session) => {
    if (knownIds.has(session.sessionId)) return
    knownIds.add(session.sessionId)
    recentSessions.value.push(session)
  })
}

const syncCurrentSessionTitle = () => {
  const currentSession = appStore.aiSalesMentorSession
  if (!currentSession?.sessionId) return
  const matched = recentSessions.value.find(
    (session) => session.sessionId === currentSession.sessionId
  )
  if (matched?.title && matched.title !== currentSession.title) {
    appStore.setAiSalesMentorSession({ ...currentSession, ...matched })
  }
}

const loadSessions = (targetPage = sessionPage.value, refreshFirstPage = false) => {
  sessionsLoading.value = true
  sessionsRequest = (async () => {
    try {
      const res = await getSessionList({ page: targetPage, pageSize: sessionPageSize })
      if (isUnmounted) return false
      const sessionList = res.result?.sessionList || []
      if (refreshFirstPage) {
        mergeFirstPageSessions(sessionList)
      } else {
        appendSessions(sessionList)
        sessionPage.value = targetPage
      }
      syncCurrentSessionTitle()
      const rowsCount = res.result?.pageHelper?.rowsCount
      sessionsFinished.value =
        (!refreshFirstPage && sessionList.length === 0) ||
        (rowsCount != null
          ? recentSessions.value.length >= rowsCount
          : sessionList.length < sessionPageSize)
      return true
    } catch (error) {
      console.error('获取会话列表失败：', error)
      return false
    } finally {
      sessionsLoading.value = false
      await nextTick()
      emits('updated')
    }
  })()
  return sessionsRequest
}

const refreshFirstPageSessions = async () => {
  if (isUnmounted) return
  try {
    // 等待当前分页请求完成，避免第一页刷新与加载更多覆盖彼此的结果。
    await sessionsRequest
    if (!isUnmounted) await loadSessions(1, true)
  } finally {
    await nextTick()
    emits('updated')
  }
}

const handleLoadMore = () => {
  if (isUnmounted || sessionsLoading.value || sessionsFinished.value) return
  return loadSessions(sessionPage.value + 1)
}

const handleClickSession = (session) => {
  emits('selected')
  if (appStore.aiSalesMentorSession?.sessionId === session.sessionId) return

  if (session.isRead === 0) {
    updateSession({
      id: session.sessionId,
      isRead: 1
    }).finally(() => {
      refreshFirstPageSessions()
    })
  }
  appStore.setAiSalesMentorSession(session)
  appStore.setOpenAiSessionId(session.sessionId)

  if (route.name === (props.mobile ? 'qcaChat' : 'aiAgent')) {
    emitter.emit('openSession', session.sessionId)
  } else {
    emits('handleOpenAiAgent')
  }
}

const handlePageActive = () => {
  if (document.visibilityState !== 'visible') return
  // 合并切回页面时相邻触发的 visibilitychange 和 focus 事件。
  clearTimeout(pageActiveTimer)
  pageActiveTimer = setTimeout(() => {
    if (document.visibilityState === 'visible') refreshFirstPageSessions()
  }, 100)
}

onMounted(() => {
  if (!props.mobile) {
    emitter.on('openAiAgentSession', handleClickSession)
    emitter.on('refreshAiAgentSessions', refreshFirstPageSessions)
    document.addEventListener('visibilitychange', handlePageActive)
    window.addEventListener('focus', handlePageActive)
  }
  // utils.xbbLocalStorageSet('instanceKey', 'sales-mentor')
  loadSessions()
})
onBeforeUnmount(() => {
  isUnmounted = true
  if (!props.mobile) {
    emitter.off('openAiAgentSession', handleClickSession)
    emitter.off('refreshAiAgentSessions', refreshFirstPageSessions)
    document.removeEventListener('visibilitychange', handlePageActive)
    window.removeEventListener('focus', handlePageActive)
    clearTimeout(pageActiveTimer)
  }
})

defineExpose({
  loadMore: handleLoadMore,
  refresh: refreshFirstPageSessions,
  sessionsList: recentSessions.value
})
</script>

<style lang="scss" scoped>
.ai-menu-v2__recent {
  padding-top: 4px;
  margin-top: 12px;
  .is-mobile {
    height: 36px;
  }
}
.ai-menu-v2__recent-title {
  padding: 6px 12px;
  font-size: 12px;
  line-height: 18px;
  color: #858e9f;
  flex-shrink: 0;
}
.ai-menu-v2__recent-list {
  margin-top: 2px;
}
.ai-menu-v2__session-status {
  display: block;
  width: 100%;
  padding: 8px 12px;
  box-sizing: border-box;
  text-align: center;
  color: #858e9f;
  font-size: 12px;
}
.ai-menu-v2__recent-item {
  width: 100%;
  text-align: left;
  display: flex;
  align-items: center;
  padding: 4px 4px 4px 12px;
  border-radius: 12px;
  min-height: 32px;
  box-sizing: border-box;
  i {
    flex-shrink: 0;
    margin-right: 8px;
    font-size: 16px;
    line-height: 16px;
    color: #646d7d;
  }
}
.ai-menu-v2__recent-item + .ai-menu-v2__recent-item {
  margin-top: 2px;
}
.right-box {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
}
.ai-menu-v2__unread {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #7a52f4;
}

.ai-menu-v2__recent {
  button {
    font: inherit;
    color: inherit;
    cursor: pointer;
    border: 0;
    background: transparent;
  }
  button:focus-visible {
    outline: 2px solid #7a52f4;
    outline-offset: -2px;
  }
  .ai-menu-v2__text {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ai-menu-v2__recent-item.active {
    background: #e1e1e1;
  }
  @media (hover: hover) and (pointer: fine) {
    &:not(.is-mobile) .ai-menu-v2__recent-item:hover {
      background: #e1e1e1;
    }
  }
}
</style>
